import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PushToggle } from "@/components/PushToggle";
import { ProximityWatcher } from "@/components/ProximityWatcher";
import { hasNotifOptIn, setNotifOptIn, clearJoin } from "@/lib/pwa";
import { isPushSupported, getPushStatus, enablePush } from "@/lib/push";
import { ClipboardCheck, PlusCircle, History, ChevronRight, Trophy, Hospital, CalendarPlus, Car, UserPlus, CalendarDays, ShieldAlert, Clock3, Moon } from "lucide-react";

export default function DriverHome() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [count, setCount] = useState(null);
  const [mine, setMine] = useState(0);
  const [points, setPoints] = useState(null);
  const [guardsAccess, setGuardsAccess] = useState(false);
  const [apptEnabled, setApptEnabled] = useState(false);
  const [prox, setProx] = useState(false);
  const [vehEnabled, setVehEnabled] = useState(false);
  const [eqEnabled, setEqEnabled] = useState(false);
  const [eqUpdate, setEqUpdate] = useState(false);
  const [clientsEnabled, setClientsEnabled] = useState(false);
  const [leavesEnabled, setLeavesEnabled] = useState(false);
  const [hoursEnabled, setHoursEnabled] = useState(false);
  const [v2Enabled, setV2Enabled] = useState(false);

  useEffect(() => {
    api.get("/requests/grouped").then((r) => setCount(r.data.total)).catch(() => setCount(0));
    api.get("/appointments/module-status").then((r) => { setApptEnabled(!!r.data.enabled); setProx(!!r.data.proximity_enabled); setVehEnabled(!!r.data.vehicle_reports_enabled); setEqEnabled(!!r.data.vehicle_equipment_enabled); setEqUpdate(!!r.data.vehicle_equipment_driver_update); setClientsEnabled(!!r.data.new_clients_enabled); setLeavesEnabled(!!r.data.leaves_enabled); setHoursEnabled(!!r.data.timesheets_enabled); setV2Enabled(!!r.data.planning_v2_enabled); }).catch(() => {});
    api.get("/requests/mine").then((r) => setMine((Array.isArray(r.data) ? r.data : []).filter((x) => x.display_status === "pris_en_charge").length)).catch(() => {});
    api.get("/points/me").then((r) => setPoints(r.data)).catch(() => {});
    api.get("/staff-planning/me/access").then((r) => setGuardsAccess(!!r.data.can_open_guard_prefs)).catch(() => setGuardsAccess(false));
    // Autorisation donnée lors de l'installation : on finalise l'abonnement push maintenant que la session existe.
    if (hasNotifOptIn() && isPushSupported() && Notification.permission === "granted") {
      getPushStatus().then((s) => { if (!s.subscribed) return enablePush(); }).then(() => setNotifOptIn(false)).catch(() => {});
    }
    clearJoin();
  }, []);

  const actions = [
    { key: "mine", label: "Mes documents à récupérer", sub: `${mine} à récupérer`, icon: ClipboardCheck, to: "/driver/mes-recuperations", cls: "bg-brand-700", testid: "action-my-pickups" },
    ...(apptEnabled ? [{ key: "appt", label: "Ajouter un rendez-vous", sub: "Transmettre à la régulation", icon: CalendarPlus, to: "/driver/rendez-vous", cls: "bg-green-600", testid: "action-appointment" }] : []),
    ...(vehEnabled ? [{ key: "veh", label: "Signaler un problème véhicule", sub: "Entretien, pneus, voyant, bruit…", icon: Car, to: "/driver/vehicule", cls: "bg-slate-700", testid: "action-vehicle-report" }] : []),
    ...(eqEnabled ? [{ key: "equip", label: "Échéances véhicules", sub: eqUpdate ? "Consulter et mettre à jour les dates matériel" : "Kits, DAE, validités par véhicule", icon: ShieldAlert, to: "/driver/equipements", cls: "bg-slate-600", testid: "action-vehicle-equipment" }] : []),
    ...(clientsEnabled ? [{ key: "client", label: "Nouveau client", sub: "Fiche administrative, scan Vitale / mutuelle", icon: UserPlus, to: "/driver/nouveau-client", cls: "bg-accent-600", testid: "action-new-client" }] : []),
    ...(leavesEnabled ? [{ key: "leave", label: "Congés & demandes", sub: "Congé, acompte, rendez-vous interne, demande diverse", icon: CalendarDays, to: "/driver/conges", cls: "bg-brand-700", testid: "action-leaves" }] : []),
    ...(v2Enabled ? [{ key: "planning", label: "Mon planning", sub: "Journées publiées, modifications, PDF", icon: CalendarDays, to: "/driver/planning", cls: "bg-indigo-600", testid: "action-my-planning" }] : []),
    ...(hoursEnabled ? [{ key: "hours", label: "Mes heures", sub: "Pointer ma journée, semaine en cours, feuilles d'heures et frais", icon: Clock3, to: "/driver/heures", cls: "bg-teal-700", testid: "action-hours" }] : []),
    ...(guardsAccess ? [{ key: "guards", label: "Mes préférences de garde", sub: "Jour, soir, nuit, indisponibilités impératives", icon: Moon, to: "/driver/preferences-garde", cls: "bg-indigo-700", testid: "action-guard-prefs" }] : []),
    { key: "report", label: "Signaler une PMT manquante", sub: "Découverte pendant un transport", icon: PlusCircle, to: "/driver/signaler", cls: "bg-orange-500", testid: "action-report" },
    { key: "hist", label: "Historique récent", sub: "Dernières tentatives", icon: History, to: "/driver/historique", cls: "bg-slate-500", testid: "action-history" },
  ];

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-semibold tracking-widest uppercase text-accent-700" data-testid="home-org">{user?.organization_name || "SanitDocs"}</p>
        <h1 className="text-2xl font-heading font-extrabold text-brand-900 mt-0.5" data-testid="home-greeting">Bonjour {user?.first_name}</h1>
      </div>

      {prox && <div className="flex justify-end"><ProximityWatcher orgEnabled={prox} userEnabled={user?.proximity_alerts_enabled} /></div>}
      <button data-testid="today-indicator" onClick={() => nav("/driver/a-recuperer")}
        className="w-full text-left rounded-2xl bg-brand-800 brand-grid text-white p-5 shadow-lg shadow-brand-900/20 relative overflow-hidden active:scale-[0.98] transition-transform">
        <Hospital className="absolute right-4 top-4 w-20 h-20 text-white/10" />
        <div className="flex items-baseline gap-2">
          <span className="text-6xl font-heading font-extrabold" data-testid="today-count">{count === null ? "…" : count}</span>
          <span className="text-brand-100 text-lg font-semibold mb-1">document{count > 1 ? "s" : ""} à récupérer aujourd'hui</span>
        </div>
        <span className="inline-flex items-center gap-1 mt-3 text-sm font-bold text-accent-200">Voir par établissement <ChevronRight className="w-4 h-4" /></span>
      </button>

      <button data-testid="driver-points-card" onClick={() => nav("/driver/mes-points")}
        className="w-full bg-white rounded-2xl border border-slate-200 p-4 shadow-sm text-left active:scale-[0.98] transition-transform">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-accent-50 text-accent-700 flex items-center justify-center shrink-0"><Trophy className="w-6 h-6" /></div>
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-brand-900">Mes points</p>
            <p className="text-sm text-slate-500">{points ? `${points.total} / ${points.objective} pts ce mois` : "…"}</p>
          </div>
          <ChevronRight className="w-6 h-6 text-slate-300" />
        </div>
        {points && (
          <div className="mt-3 h-2.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-accent-500 transition-all" style={{ width: `${points.percent}%` }} />
          </div>
        )}
      </button>

      <div className="grid grid-cols-1 gap-3">
        {actions.map((a) => (
          <button key={a.key} data-testid={a.testid} onClick={() => nav(a.to)}
            className="w-full bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-4 shadow-sm touch-manipulation active:scale-[0.98] transition-transform text-left min-h-[80px]">
            <div className={`w-14 h-14 rounded-xl ${a.cls} text-white flex items-center justify-center shrink-0 shadow-sm`}><a.icon className="w-7 h-7" /></div>
            <div className="flex-1">
              <p className="text-lg font-heading font-bold text-brand-900 leading-tight">{a.label}</p>
              <p className="text-sm text-slate-500">{a.sub}</p>
            </div>
            <ChevronRight className="w-6 h-6 text-slate-300" />
          </button>
        ))}
      </div>

      <div className="pt-1"><PushToggle variant="chip" /></div>
    </div>
  );
}
